<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import Column from 'primevue/column';
import DatePicker from 'primevue/datepicker';
import AutoComplete, { type AutoCompleteCompleteEvent } from 'primevue/autocomplete';
import { Search } from '@lucide/vue';
import {
  CategoriaAudit,
  ETICHETTE_CATEGORIA_AUDIT,
  GIORNI_CONSERVAZIONE_AUDIT,
  type AuditLogQuery,
  type AuditLogVoce,
  type Utente,
} from '@evolfin/shared';
import { useListaQuery } from '@/composables/useListaQuery';
import TabellaDati from '@/components/ui/TabellaDati.vue';
import AvatarUtente from '@/components/ui/AvatarUtente.vue';
import { formattaDataOra } from '@/utils/formato';
import { listaUtenti } from '@/features/utenti/api';
import NavImpostazioni from '../components/NavImpostazioni.vue';
import { listaRegistroAttivita } from '../api';
import { metaAzione, riepilogoVoce, soggettoVoce } from '../registro-attivita';

definePage({ meta: { titolo: 'Registro attività', privilegi: ['can_read_audit_log'] } });

const { righe, totale, query, caricamento, onPage, onSort, cerca } = useListaQuery<AuditLogVoce>(
  'registro-attivita',
  (q) => listaRegistroAttivita(q as AuditLogQuery),
);
const filtri = query as Partial<AuditLogQuery>;

const testoRicerca = ref('');

const categoria = computed<string>({
  get: () => filtri.categoria ?? '',
  set: (v) => {
    filtri.categoria = v ? (v as CategoriaAudit) : undefined;
    query.page = 1;
  },
});

// Chi ha agito: lo staff cresce, quindi si cerca sul server, disattivati compresi
// (le loro azioni restano nel registro).
const utenteScelto = ref<Utente | string | null>(null);
const suggerimentiUtenti = ref<Utente[]>([]);

async function cercaUtenti(evento: AutoCompleteCompleteEvent): Promise<void> {
  const pagina = await listaUtenti({
    page: 1,
    pageSize: 20,
    order: 'asc',
    sort: 'cognome',
    stato: 'tutti',
    duefa: 'tutti',
    q: (evento.query ?? '').trim() || undefined,
  });
  suggerimentiUtenti.value = pagina.items;
}

watch(utenteScelto, (u) => {
  // Mentre si digita il modello è la stringa cercata: il filtro cambia solo
  // quando si sceglie qualcuno o si svuota il campo.
  if (typeof u === 'string' && u) return;
  const id = u && typeof u === 'object' ? u.id : undefined;
  if (id === filtri.utenteId) return;
  filtri.utenteId = id;
  query.page = 1;
});

// Periodo: il DatePicker lavora su `Date`, il filtro su giorni ISO locali.
const periodo = ref<(Date | null)[] | null>(null);

function giornoIso(d: Date): string {
  const due = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${due(d.getMonth() + 1)}-${due(d.getDate())}`;
}

watch(periodo, (valore) => {
  const [inizio, fine] = valore ?? [];
  filtri.dal = inizio ? giornoIso(inizio) : undefined;
  filtri.al = inizio ? giornoIso(fine ?? inizio) : undefined;
  query.page = 1;
});

const opzioniCategoria = CategoriaAudit.options.map((c) => ({
  valore: c,
  etichetta: ETICHETTE_CATEGORIA_AUDIT[c],
}));
</script>

<template>
  <div class="flex flex-col gap-6">
    <header>
      <p class="text-xs font-semibold tracking-[0.08em] text-ink-muted uppercase">Impostazioni</p>
      <h1 class="mt-1 font-display text-[26px] leading-tight font-semibold">Registro attività</h1>
      <p class="mt-1 text-[13px] text-ink-secondary">
        Accessi, utenti e ruoli, export ed eliminazioni. Le voci restano
        {{ GIORNI_CONSERVAZIONE_AUDIT }} giorni; chi ha caricato o scaricato un file lo dice lo
        storico del file stesso.
      </p>
    </header>

    <NavImpostazioni />

    <div class="card p-0">
      <div class="flex flex-wrap items-center gap-3 border-b border-line-soft px-4 py-3">
        <label class="relative min-w-[14rem] flex-1">
          <Search
            class="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-muted"
            :stroke-width="2"
            aria-hidden="true"
          />
          <input
            v-model="testoRicerca"
            type="search"
            placeholder="Cerca per cliente, utente, ruolo…"
            aria-label="Cerca nel registro"
            class="w-full rounded-sm border border-line bg-surface py-2 pr-3 pl-9 text-[13px] text-ink transition-[border-color] focus:border-[var(--color-accent)]"
            @input="cerca(testoRicerca)"
          />
        </label>

        <select v-model="categoria" class="filtro" aria-label="Filtra per tipo di attività">
          <option value="">Tutte le attività</option>
          <option v-for="c in opzioniCategoria" :key="c.valore" :value="c.valore">
            {{ c.etichetta }}
          </option>
        </select>

        <AutoComplete
          v-model="utenteScelto"
          :suggestions="suggerimentiUtenti"
          :option-label="(u: Utente) => `${u.nome} ${u.cognome}`"
          placeholder="Chiunque"
          aria-label="Filtra per chi ha agito"
          :min-length="0"
          :delay="350"
          complete-on-focus
          force-selection
          show-clear
          class="w-full sm:w-56"
          fluid
          @complete="cercaUtenti"
        />

        <DatePicker
          v-model="periodo"
          selection-mode="range"
          :manual-input="false"
          date-format="dd/mm/yy"
          placeholder="Qualsiasi periodo"
          aria-label="Filtra per periodo"
          show-icon
          icon-display="input"
          show-button-bar
          class="w-full sm:w-60"
          fluid
        />
      </div>

      <TabellaDati
        :righe="righe"
        :totale="totale"
        :query="query"
        :caricamento="caricamento"
        @page="onPage"
        @sort="onSort"
      >
        <Column header="Attività" primaria>
          <template #body="{ data }: { data: AuditLogVoce }">
            <div class="min-w-0">
              <p class="font-medium text-ink">{{ metaAzione(data.azione).etichetta }}</p>
              <p v-if="soggettoVoce(data)" class="text-[12.5px] text-ink-secondary">
                {{ soggettoVoce(data) }}
              </p>
            </div>
          </template>
        </Column>

        <Column header="Chi">
          <template #body="{ data }: { data: AuditLogVoce }">
            <div v-if="data.utente" class="flex items-center gap-2">
              <AvatarUtente
                :id="data.utente.id"
                :nome="data.utente.nome"
                :cognome="data.utente.cognome"
                :dimensione="26"
              />
              <span class="text-[13px] text-ink">
                {{ data.utente.nome }} {{ data.utente.cognome }}
              </span>
            </div>
            <span v-else class="text-[12.5px] text-ink-muted">Utente sconosciuto</span>
          </template>
        </Column>

        <Column header="Dettagli">
          <template #body="{ data }: { data: AuditLogVoce }">
            <p
              v-if="riepilogoVoce(data).length"
              class="max-w-[28rem] text-[12.5px] break-words text-ink-secondary"
            >
              {{ riepilogoVoce(data).join(' · ') }}
            </p>
            <span v-else class="text-[12px] text-ink-muted">—</span>
          </template>
        </Column>

        <Column header="Quando">
          <template #body="{ data }: { data: AuditLogVoce }">
            <span class="text-[12.5px] whitespace-nowrap text-ink-secondary tabular-nums">
              {{ formattaDataOra(data.creatoIl) }}
            </span>
          </template>
        </Column>

        <template #vuoto>
          <p class="py-10 text-center text-[13px] text-ink-secondary">
            Nessuna attività con questi filtri.
          </p>
        </template>
      </TabellaDati>
    </div>
  </div>
</template>

<style scoped>
.filtro {
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border);
  /* `background-color`, non la shorthand `background`: quest'ultima azzererebbe
     il `background-image` (la chevron globale sui `<select>`). */
  background-color: var(--color-surface);
  /* Solo padding sinistro/verticale: quello destro lo riserva la regola base
     sui `<select>` per la freccia. */
  padding-block: 0.5rem;
  padding-inline-start: 0.65rem;
  font-size: 13px;
  color: var(--color-text);
  transition: border-color 120ms ease;
}
.filtro:focus {
  outline: none;
  border-color: var(--color-accent);
}
</style>
